// Lucky Bread Order Management - TypeScript Data Models

// User & Authentication
export class LoginDetails {
  username: string;
  password: string;
}

export class UserSession {
  sessionId: string;
  userId: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roles: string[];
  customerId?: number;
  linkedCustomers?: LinkedCustomer[];
}

// Linked customer info for users managing multiple wholesale locations
export class LinkedCustomer {
  id: number;
  customerName: string;
  customerCode: string;
  city: string;
  isDefault: boolean;
}

export class LinkUserCustomerRequest {
  userId: number;
  customerId: number;
  isDefault: boolean;
}

// ---- Device pairing (Android TV / kiosk APK) ----

export class DevicePairingCode {
  deviceCode: string;
  userCode: string;
  qrPayloadUrl: string;
  expiresInSeconds: number;
  pollIntervalSeconds: number;
}

export class DevicePairingStatus {
  status: string; // "Pending" | "Approved" | "Expired"
  sessionId?: string;
  defaultView?: string;
  deviceName?: string;
}

export class ApprovePairingRequest {
  userCode: string;
  deviceName: string;
  defaultView: string;
}

export class ApprovePairingResponse {
  success: boolean;
  deviceName: string;
}

export class SystemUser {
  id: number;
  userName: string;
  email: string;
  firstName: string;
  lastName: string;
  isEnabled: boolean;  // QIN uses IsEnabled, not IsActive
  accountLocked: boolean;
  passwordFailedCount: number;
  createdDate?: Date;
}

export class CreateUserRequest {
  username: string;
  password: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

// Customer
export class Customer {
  id: number;
  customerName: string;
  customerCode: string;
  contactPerson: string;
  email: string;
  phone: string;
  alternatePhone?: string;
  deliveryAddress: string;
  billingAddress?: string;
  city: string;
  postalCode: string;
  customerType: string;
  isActive: boolean;
  notes?: string;
  preferredDeliveryDays?: string;
  preferredDeliveryTime?: string;
  creditLimit?: number;
  currentBalance?: number;
  sageCustomerId?: string;
  /** When true, customer can add free-form "Other" lines (with a required description) to an order. */
  allowOtherItems?: boolean;
  createdDate?: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;
}

// Product Access
export class ProductAccessInfo {
  productId: number;
  hasRestrictions: boolean;
  allowedCustomers: CustomerSummary[];
}

export class CustomerSummary {
  id: number;
  customerName: string;
  customerCode: string;
}

// Product
export class Product {
  id: number;
  productName: string;
  productCode: string;
  description?: string;
  category: string;
  subCategory?: string;
  unitPrice: number;
  unit: string;
  unitsPerPack?: number;
  isActive: boolean;
  isAvailable: boolean;
  minOrderQuantity?: number;
  maxOrderQuantity?: number;
  currentStock?: number;
  reorderLevel?: number;
  parMinimumStock?: number;  // PAR minimum - trigger point for reorder
  parReorderLevel?: number;  // PAR target - quantity to produce to
  leadTimeHours?: number;
  imageUrl?: string;
  displayOrder: number;
  nutritionalInfo?: string;
  ingredients?: string;
  allergens?: string;  // Comma-separated list of allergens
  quantityInputType?: string;  // WholeNumber, Decimal, Custom
  quantityIncrement?: number;  // For Decimal type
  allowedQuantities?: string;  // For Custom type, comma-separated
  isKit?: boolean;  // True if this is a kit (composite product)
  /** FinishedGood (sold) or SubAssembly (made in-house, used in other products). ProductStockTypes. */
  stockType?: string;
  isSubAssembly?: boolean;
  createdDate?: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;
}

// ========== Kit Products ==========

/**
 * Full kit definition for a product: fixed (always-included) components plus
 * option groups with selectable candidate items. Fetched from
 * GET /api/product/{id}/kit and saved via PUT /api/product/{id}/kit.
 */
export interface KitDefinition {
  kitProductId: number;
  kitProductName?: string;
  isKit: boolean;
  fixedComponents: KitFixedComponent[];
  optionGroups: KitOptionGroup[];
}

export interface KitFixedComponent {
  id: number;  // 0 for new
  componentProductId: number;
  componentProductName?: string;
  componentProductCode?: string;
  componentCategory?: string;
  quantity: number;
  displayOrder: number;
}

export interface KitOptionGroup {
  id: number;  // 0 for new
  groupName: string;
  minSelections: number;
  maxSelections: number;
  displayOrder: number;
  items: KitOptionItem[];
}

export interface KitOptionItem {
  id: number;  // 0 for new
  componentProductId: number;
  componentProductName?: string;
  componentProductCode?: string;
  componentCategory?: string;
  quantityPerSelection: number;
  displayOrder: number;
}

/**
 * A single customer-chosen option, sent to the server as part of
 * OrderItemRequest.kitSelections when creating or updating an order.
 */
export interface KitSelection {
  optionGroupId: number;
  componentProductId: number;
  quantityPerKit: number;
}

/**
 * Read-only display form returned by GET /api/order/item/{id}/kit-selections,
 * used when loading an existing order line for edit.
 */
export interface KitSelectionDisplay {
  optionGroupId: number;
  optionGroupName: string;
  componentProductId: number;
  componentProductName: string;
  componentProductCode: string;
  quantityPerKit: number;
}

/**
 * A single row in the flat, kit-expanded component demand list returned by
 * GET /api/factory/component-demand. Non-kit items appear with sourceKitProductId=null;
 * kit items produce one row per fixed component and one row per selected option.
 */
export interface KitComponentDemand {
  componentProductId: number;
  componentProductName: string;
  componentProductCode: string;
  componentCategory: string;
  quantity: number;
  sourceOrderItemId: number;
  sourceKitProductId?: number;
  sourceKitProductName?: string;
  isFromKit: boolean;
}

// Order
export class Order {
  id: number;
  orderNumber: string;
  customer: Customer;
  currentStatus: string;
  orderDate: Date;
  requestedDeliveryDate: Date;
  requestedDeliveryTime?: string;
  actualDeliveryDate?: Date;
  totalAmount: number;
  discountAmount?: number;
  taxAmount?: number;
  grandTotal: number;
  placedByUser?: SystemUser;
  submittedDate?: Date;
  approvedDate?: Date;
  approvedBy?: string;
  statusReason?: string;
  notes?: string;
  deliveryNotes?: string;
  deliveryAddress?: string;
  shippedDate?: Date;
  shippedBy?: string;
  isPriority: boolean;
  isNewOrder: boolean;
  orderType?: string;  // 'Regular' | 'Replacement'
  originalOrder?: Order;  // For replacement orders
  originalShipment?: Shipment;  // For replacement orders
  createdDate?: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;
  billingStatus?: string;
  sageOrderNumber?: string;
  lastExportedDate?: Date;
}

export interface FinanceOrderDto {
  orderId: number;
  orderNumber: string;
  customerName: string;
  customerCode: string;
  orderDate: Date;
  requestedDeliveryDate: Date;
  totalAmount: number;
  grandTotal: number;
  currentStatus: string;
  billingStatus: string;
  orderType?: string;
  sageOrderNumber?: string;
  lastExportedDate?: Date;
}

export class OrderItem {
  id: number;
  order: Order;
  product: Product;
  quantityOrdered: number;
  quantityApproved?: number;
  quantityReady?: number;
  quantityShipped?: number;
  unitPrice: number;
  discountPercent?: number;
  lineTotal: number;
  notes?: string;
  specialInstructions?: string;
  modificationReason?: string;
  isReady: boolean;
  createdDate?: Date;
  modifiedDate?: Date;
}

// Order Requests
export class CreateOrderRequest {
  customerId: number;
  requestedDeliveryDate: Date;
  requestedDeliveryTime?: string;
  deliveryAddress?: string;
  notes?: string;
  deliveryNotes?: string;
  isPriority: boolean;
  items: OrderItemRequest[];
}

export class OrderItemRequest {
  productId: number;
  quantity: number;
  unitPrice: number;
  specialInstructions?: string;
  /** For kit products, the customer's chosen options. Ignored for non-kit products. */
  kitSelections?: KitSelection[];
  /** When true, this is a free-form "Other" line: server resolves productId to the
   *  seeded Other product, forces unitPrice=0, and requires specialInstructions. */
  isOther?: boolean;
}

export class ApproveOrderRequest {
  notes?: string;
}

export class ModifyOrderRequest {
  reason: string;
  modifiedItems: ModifiedItemRequest[];
}

export class ModifiedItemRequest {
  orderItemId: number;
  approvedQuantity: number;
  reason?: string;
}

export class DeclineOrderRequest {
  reason: string;
}

export class CancelOrderRequest {
  reason?: string;
}

// Factory & Production
export class ProductionSummary {
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  totalQuantityNeeded: number;
  quantityReady: number;
  quantityRemaining: number;
  isComplete: boolean;
  // Optional stock order fields - when this item is from a stock production order
  isStockOrder?: boolean;
  stockOrderId?: number;
  // Kit expansion hints: present when some/all of this row's demand came from kits
  kitSources?: string[];
  directQuantity?: number;
  fromKitQuantity?: number;
  // Earliest overdue delivery date (only set on late-orders feed). Frontend uses
  // this to colour cards orange (1 day late) vs red (2+ days late).
  earliestLateDate?: string;
}

export class CustomerOrderSummary {
  customerId: number;
  customerName: string;
  customerCode: string;
  orderCount: number;
  totalItems: number;
  orders: Order[];
}

export class DailyOrderSummary {
  deliveryDate: Date;
  dayName: string;
  isToday: boolean;
  isTomorrow: boolean;
  orderCount: number;
  totalItems: number;
  orders: Order[];
  productSummary: DailyProductSummary[];
}

export class DailyProductSummary {
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  quantityNeeded: number;
}

export class UpdateReadinessRequest {
  quantityReady: number;
  notes?: string;
}

// Production Touch Screen
export class ProductionItem {
  orderItemId: number;
  orderId: number;
  orderNumber: string;
  customerName: string;
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  quantityNeeded: number;
  quantityReady: number;
  quantityRemaining: number;
  isComplete: boolean;
  deliveryDate: Date;
  isPriority: boolean;
  /** When set, this row is a component exploded from a kit line. */
  sourceKitProductId?: number;
  sourceKitProductName?: string;
}

export class ProductionGroup {
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  totalNeeded: number;
  totalReady: number;
  totalRemaining: number;
  isComplete: boolean;
  items: ProductionItem[];
}

// Packing Screen
export class PackingItem {
  orderItemId: number;
  orderId: number;
  orderNumber: string;
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  quantityOrdered: number;
  quantityReady: number;
  quantityShipped: number;
  quantityToPack: number;
  isFullyPacked: boolean;
}

export class CustomerPackingGroup {
  customerId: number;
  customerName: string;
  customerCode: string;
  city: string;
  deliveryAddress: string;
  orderCount: number;
  totalItemsToPack: number;
  items: PackingItem[];
}

// Shipment
export class Shipment {
  id: number;
  shipmentNumber: string;
  order?: Order;  // Now optional - multi-order shipments may not have a single order
  shipmentDate: Date;
  shippedBy?: string;
  driver?: Driver;  // FK to Driver entity
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  estimatedDeliveryTime?: Date;
  actualDeliveryTime?: Date;
  deliveryAddress?: string;
  deliveryStatus?: string;
  deliveryNotes?: string;
  receivedBy?: string;
  confirmationReference?: string;
  createdDate?: Date;
  createdBy?: string;
  // Invoice/Sage integration fields
  invoiceStatus?: string;
  sageInvoiceReference?: string;
  invoiceSentDate?: Date;
  invoiceSentBy?: string;
  invoiceErrorMessage?: string;
  // Customer info (computed from Order.Customer)
  customerName?: string;
  customerCode?: string;
}

export class ShipmentItem {
  id: number;
  shipment: Shipment;
  orderItem: OrderItem;
  quantityShipped: number;
  isPacked: boolean;
  createdDate?: Date;
  createdBy?: string;
}

export interface AdjustShipmentItemRequest {
  newQuantity: number;
  reason: string;
}

// Legacy single-order shipment request
export class CreateShipmentRequest {
  orderId: number;
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
}

// Create truck/shipment without orders
export class CreateTruckRequest {
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
}

// Add item to shipment with specific quantity
export class AddShipmentItemRequest {
  orderItemId: number;
  quantity: number;
}

// Add entire order to shipment
export class AddOrderToShipmentRequest {
  orderId: number;
}

// Create shipment with multiple orders at once
export class CreateMultiOrderShipmentRequest {
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  orderIds: number[];
}

export class ConfirmDeliveryRequest {
  receivedBy: string;
  notes?: string;
  actualDeliveryTime?: string;
}

export interface UpdateShipmentRequest {
  driverName?: string;
  driverPhone?: string;
  vehicleNumber?: string;
  deliveryStatus?: string;
  receivedBy?: string;
  deliveryNotes?: string;
  /** datetime-local wall-clock value, e.g. 2026-09-16T14:30 */
  actualDeliveryTime?: string;
}

// Generic Return
export class Return {
  success: boolean;
  message: string;
  data?: any;
}

// Constants
export class OrderStatuses {
  static readonly New = 'New';
  static readonly Submitted = 'Submitted';
  static readonly Approved = 'Approved';
  static readonly ApprovedModified = 'Approved (Modified)';
  static readonly Declined = 'Declined';
  static readonly InProduction = 'In Production';
  static readonly ReadyForShipment = 'Ready for Shipment';
  static readonly PartiallyShipped = 'Partially Shipped';
  static readonly Shipped = 'Shipped';
  static readonly Delivered = 'Delivered';
  static readonly Cancelled = 'Cancelled';
}

export class UserRoles {
  static readonly Admin = 'Admin';
  static readonly Staff = 'Staff';
  static readonly Customer = 'Customer';
  static readonly FactoryWorker = 'FactoryWorker';
  static readonly FactoryManager = 'FactoryManager';
  static readonly Driver = 'Driver';
  static readonly StockController = 'StockController';
}

export class ProductCategories {
  static readonly Bread = 'Bread';
  static readonly Rolls = 'Rolls';
  static readonly Pastries = 'Pastries';
  static readonly Cakes = 'Cakes';
  static readonly Pies = 'Pies';
  static readonly Buns = 'Buns';
  static readonly Specialty = 'Specialty';
  static readonly Seasonal = 'Seasonal';

  static getAll(): string[] {
    return [
      this.Bread,
      this.Rolls,
      this.Pastries,
      this.Cakes,
      this.Pies,
      this.Buns,
      this.Specialty,
      this.Seasonal
    ];
  }
}

export class CustomerTypes {
  static readonly Retail = 'Retail';
  static readonly Wholesale = 'Wholesale';
  static readonly Restaurant = 'Restaurant';
  static readonly Supermarket = 'Supermarket';
  static readonly CafeBakery = 'Cafe/Bakery';

  static getAll(): string[] {
    return [
      this.Retail,
      this.Wholesale,
      this.Restaurant,
      this.Supermarket,
      this.CafeBakery
    ];
  }
}

// Food Allergens (based on FDA/EU major allergens)
export class Allergens {
  static readonly Gluten = 'Gluten';
  static readonly Wheat = 'Wheat';
  static readonly Dairy = 'Dairy';
  static readonly Milk = 'Milk';
  static readonly Eggs = 'Eggs';
  static readonly Nuts = 'Tree Nuts';
  static readonly Peanuts = 'Peanuts';
  static readonly Soy = 'Soy';
  static readonly Sesame = 'Sesame';
  static readonly Fish = 'Fish';
  static readonly Shellfish = 'Shellfish';
  static readonly Sulphites = 'Sulphites';
  static readonly Mustard = 'Mustard';
  static readonly Celery = 'Celery';
  static readonly Lupin = 'Lupin';

  static getAll(): string[] {
    return [
      this.Gluten,
      this.Wheat,
      this.Dairy,
      this.Milk,
      this.Eggs,
      this.Nuts,
      this.Peanuts,
      this.Soy,
      this.Sesame,
      this.Fish,
      this.Shellfish,
      this.Sulphites,
      this.Mustard,
      this.Celery,
      this.Lupin
    ];
  }
}

// Packing (Stock-based) Models

export interface OrderPackingInfo {
  orderId: number;
  orderNumber: string;
  customerName: string;
  city: string;
  requestedDeliveryDate: Date;
  isPriority: boolean;
  currentStatus: string;
  totalItemsNeeded: number;
  totalItemsAvailable: number;
  readinessPercent: number;
  isFullyReady: boolean;
}

export interface OrderPackingDetails {
  orderId: number;
  orderNumber: string;
  customerName: string;
  customerAddress: string;
  requestedDeliveryDate: Date;
  isPriority: boolean;
  currentStatus: string;
  totalItemsNeeded: number;
  totalItemsAvailable: number;
  readinessPercent: number;
  items: OrderItemPackingInfo[];
}

export interface OrderItemPackingInfo {
  orderItemId: number;
  productId: number;
  productCode: string;
  productName: string;
  category: string;
  quantityOrdered: number;
  quantityShipped: number;
  quantityRemaining: number;
  quantityAvailable: number;
  /** Quantity already reserved/packed against this line (out of the free pool). */
  quantityAllocated: number;
  quantityToShip: number;
  isFullyAvailable: boolean;
  /** True if this order line is a kit; components carries the exploded breakdown. */
  isKit?: boolean;
  components?: KitComponentPackingInfo[];
}

export interface KitComponentPackingInfo {
  componentProductId: number;
  componentProductCode: string;
  componentProductName: string;
  category: string;
  /** "Fixed" for always-included components, else the kit option group name. */
  groupName: string;
  quantityPerKit: number;
  quantityRequired: number;
  quantityAvailable: number;
  isFullyAvailable: boolean;
}

export interface ShipmentItemDto {
  orderItemId: number;
  quantityToShip: number;
}

export interface CreateShipmentFromStockRequest {
  driverId?: number;
  driverName: string;
  vehicleInfo: string;
  items: ShipmentItemDto[];
}

export interface AllocateToOrderRequest {
  items: ShipmentItemDto[];
}

export interface ShipAndDeliverRequest {
  receivedBy: string;
  items: ShipmentItemDto[];
}

// Driver Management
export class Driver {
  id: number;
  driverCode: string;
  firstName: string;
  lastName: string;
  phone: string;
  alternatePhone?: string;
  email?: string;
  licenseNumber?: string;
  licenseExpiry?: Date;
  vehicleNumber?: string;
  vehicleType?: string;
  vehicleCapacity?: string;
  isActive: boolean;
  notes?: string;
  systemUser?: SystemUser;
  createdDate?: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;

  get fullName(): string {
    return `${this.firstName} ${this.lastName}`.trim();
  }
}

export class CreateDriverRequest {
  firstName: string;
  lastName: string;
  phone: string;
  alternatePhone?: string;
  email?: string;
  licenseNumber?: string;
  licenseExpiry?: Date;
  vehicleNumber?: string;
  vehicleType?: string;
  vehicleCapacity?: string;
  notes?: string;
}

export class UpdateDriverRequest extends CreateDriverRequest {
  isActive: boolean;
}

// Enhanced Delivery Confirmation
export class DeliveryConfirmation {
  id: number;
  shipment: Shipment;
  receiverName: string;
  receiverPhone?: string;
  receiverTitle?: string;
  deliveryType: string;  // 'Full' | 'Partial'
  signatureData?: string;
  signatureMethod?: string;  // 'Canvas' | 'TypedName' | 'Photo'
  confirmationNotes?: string;
  confirmedAt: Date;
  confirmedByDriver?: Driver;
  gpsLatitude?: number;
  gpsLongitude?: number;
  createdDate?: Date;
  createdBy?: string;
}

export class EnhancedDeliveryRequest {
  shipmentId: number;
  receiverName: string;
  receiverPhone?: string;
  receiverTitle?: string;
  signatureData?: string;
  signatureMethod?: string;
  notes?: string;
  gpsLatitude?: number;
  gpsLongitude?: number;
  damagedItems?: DamagedItemRequest[];
}

export class DamagedItemRequest {
  shipmentItemId: number;
  quantityDamaged: number;
  damageType?: string;  // 'Crushed' | 'Broken' | 'Spoiled' | 'Other'
  damageDescription?: string;
  resolution: string;  // 'Return' | 'Replace' | 'Credit' | 'Accept'
  photoData?: string;
}

export class DamagedItem {
  id: number;
  shipmentItem: ShipmentItem;
  deliveryConfirmation: DeliveryConfirmation;
  quantityDamaged: number;
  damageType?: string;
  damageDescription?: string;
  resolution: string;
  photoData?: string;
  replacementOrder?: Order;
  returnedToStock: boolean;
  processedDate?: Date;
  processedBy?: string;
  createdDate?: Date;
  createdBy?: string;
}

export class ShipmentDetailsDto {
  shipment: Shipment;
  items: ShipmentItem[];
  customer?: Customer;
  orders?: Order[];
}

export class DriverShipmentSummary {
  totalAssigned: number;
  pendingDeliveries: number;
  inTransit: number;
  completedToday: number;
  upcomingDeliveries: Shipment[];
}

export class ProcessReturnRequest {
  returnToStock: boolean;
}

/** Goods a driver brought back that still need a decision */
export interface ReturnToProcess {
  damagedItemId: number;
  productName: string;
  productCode: string;
  isKit: boolean;
  quantity: number;
  damageType: string;
  damageDescription?: string;
  orderNumber: string;
  customerName: string;
  shipmentNumber: string;
  reportedDate: string;
}

/** Raised for damaged goods resolved as Credit; exported to Sage like an order */
export interface FinanceCreditNoteDto {
  creditNoteId: number;
  creditNoteNumber: string;
  orderId: number;
  orderNumber: string;
  customerName: string;
  customerCode: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  reason?: string;
  billingStatus: string;
  sageReference?: string;
  createdDate: string;
  lastExportedDate?: string | Date;
}

// New Constants
export class DeliveryTypes {
  static readonly Full = 'Full';
  static readonly Partial = 'Partial';
}

export class DamageTypes {
  static readonly Crushed = 'Crushed';
  static readonly Broken = 'Broken';
  static readonly Spoiled = 'Spoiled';
  static readonly Other = 'Other';

  static getAll(): string[] {
    return [this.Crushed, this.Broken, this.Spoiled, this.Other];
  }
}

export class DamageResolutions {
  static readonly Return = 'Return';
  static readonly Replace = 'Replace';
  static readonly Credit = 'Credit';
  static readonly Accept = 'Accept';

  static getAll(): string[] {
    return [this.Return, this.Replace, this.Credit, this.Accept];
  }
}

export class OrderTypes {
  static readonly Regular = 'Regular';
  static readonly Replacement = 'Replacement';
}

export class SignatureMethods {
  static readonly Canvas = 'Canvas';
  static readonly TypedName = 'TypedName';
  static readonly Photo = 'Photo';
}

// Factory View Groups
export class FactoryViewGroup {
  id: number;
  groupName: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  productCount?: number;
  productIds?: number[];
  createdDate?: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;
}

export class CreateFactoryViewGroupRequest {
  groupName: string;
  description?: string;
  displayOrder: number;
  productIds?: number[];
}

export class UpdateFactoryViewGroupRequest {
  groupName: string;
  description?: string;
  displayOrder: number;
  isActive: boolean;
  productIds?: number[];
}

export class WeeklyProductionSummary {
  date: Date;
  dayName: string;
  isToday: boolean;
  isTomorrow: boolean;
  totalProducts: number;
  totalItems: number;
  products: ProductionSummary[];
  /** Set client-side when the card is re-labeled by production day (date - 1). */
  deliveryDate?: Date;
}

// Priority orders - orders with delivery time or marked as priority
export interface PriorityOrderGroup {
  orderId: number;
  orderNumber: string;
  customerName: string;
  customerCode: string;
  deliveryTime: string;
  isPriority: boolean;
  items: PriorityOrderItem[];
}

export interface PriorityOrderItem {
  productId: number;
  productName: string;
  productCode: string;
  quantityNeeded: number;
  quantityReady: number;
  quantityRemaining: number;
  isComplete: boolean;
}

// ============ FULL ORDER DETAILS ============

export interface OrderFullDetailsDto {
  order: Order;
  items: OrderItem[];
  /** The items as the kitchen makes them: kit lines broken into their components. */
  productionItems?: OrderItem[];
  shipments: ShipmentWithDetailsDto[];
  damagedItems: DamagedItem[];
  history: OrderHistoryEntry[];
}

export interface ShipmentWithDetailsDto {
  shipment: Shipment;
  items: ShipmentItem[];
  driver?: Driver;
  confirmation?: DeliveryConfirmation;
}

export interface OrderHistoryEntry {
  timestamp: Date;
  event: string;
  description: string;
  user: string;
}

export interface AdminUpdateOrderRequest {
  requestedDeliveryDate?: Date;
  requestedDeliveryTime?: string;
  deliveryAddress?: string;
  deliveryNotes?: string;
  notes?: string;
  isPriority?: boolean;
  items?: AdminUpdateItemRequest[];
}

export interface AdminUpdateItemRequest {
  orderItemId: number;  // 0 for new items
  productId?: number;   // for new items
  quantity: number;
  specialInstructions?: string;
  modificationReason?: string;
  delete: boolean;  // true to remove item
}

// ============ SCRAP/WASTAGE ============

export interface ScrapItem {
  id: number;
  product: Product;
  quantityScrapped: number;
  reasonCode: string;
  reasonDescription?: string;
  scrapDate: Date;
  estimatedValue: number;
  enteredBy?: string;
  isProcessed: boolean;
  processedDate?: Date;
  processedBy?: string;
  createdDate?: Date;
  createdBy?: string;
}

export interface CreateScrapRequest {
  productId: number;
  quantity: number;
  reasonCode: string;
  reasonDescription?: string;
  estimatedValue?: number;
}

export interface ScrapSummary {
  todayCount: number;
  weekCount: number;
  monthCount: number;
  todayValue: number;
  monthValue: number;
}

export interface MonthlyScrapSummary {
  month: number;
  monthName: string;
  count: number;
  value: number;
}

export interface YearlyScrapReport {
  year: number;
  months: MonthlyScrapSummary[];
  totalCount: number;
  totalValue: number;
}

export class ScrapReasonCodes {
  static readonly Burnt = 'Burnt';
  static readonly Dropped = 'Dropped';
  static readonly Expired = 'Expired';
  static readonly Quality = 'Quality';
  static readonly Equipment = 'Equipment';
  static readonly Other = 'Other';

  static getAll(): string[] {
    return [this.Burnt, this.Dropped, this.Expired, this.Quality, this.Equipment, this.Other];
  }
}

// ============ STOCK MANAGEMENT ============

export interface ProductStockInfo {
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  currentStock: number;
  totalProduced: number;
  totalAllocated: number;
  producedToday: number;
  // PAR fields
  parMinimumStock: number;
  parReorderLevel: number;
  quantityToOrder: number;
  pendingProductionQuantity: number;
  isBelowPar: boolean;
}

// ============ STOCK PRODUCTION ORDERS ============

export interface StockProductionOrder {
  id: number;
  orderNumber: string;
  product: Product;
  quantityToProduced: number;
  quantityProduced: number;
  status: string;
  stockAtCreation: number;
  targetStockLevel: number;
  isAutoGenerated: boolean;
  priority: string;
  notes: string;
  createdDate: Date;
  createdBy: string;
  startedDate: Date;
  startedBy: string;
  completedDate: Date;
  completedBy: string;
  quantityRemaining: number;
  isComplete: boolean;
  progressPercent: number;
}

export interface CreateStockProductionOrderRequest {
  productId: number;
  quantity: number;
  priority?: string;
  notes?: string;
}

export interface StockAdjustment {
  id: number;
  product: Product;
  quantityChange: number;
  adjustmentType: string;
  direction: string;
  reference?: string;
  reason?: string;
  reasonCode?: string;
  balanceAfter: number;
  adjustmentDate: Date;
  adjustedBy?: string;
}

export interface StockFlowSummary {
  productId: number;
  productName: string;
  productCode: string;
  totalIn: number;
  totalOut: number;
  netChange: number;
  productionIn: number;
  shipmentOut: number;
  scrapOut: number;
  manualAdjustments: number;
}

export interface ManualAdjustmentRequest {
  productId: number;
  quantity: number;
  direction: string;
  reason: string;
  reasonCode?: string;
}

export interface StockCountRequest {
  productId: number;
  countedQuantity: number;
  notes?: string;
}

export class StockAdjustmentTypes {
  static readonly Production = 'Production';
  static readonly Shipment = 'Shipment';
  static readonly StockCount = 'StockCount';
  static readonly Manual = 'Manual';
  static readonly Scrap = 'Scrap';
  /** A sub-assembly used up making another product (backflush) */
  static readonly Consumed = 'Consumed';
  /** Goods a customer sent back, put back on the shelf */
  static readonly Return = 'Return';

  static getAll(): string[] {
    return [this.Production, this.Shipment, this.StockCount, this.Manual, this.Scrap, this.Consumed, this.Return];
  }
}

export class StockDirections {
  static readonly In = 'In';
  static readonly Out = 'Out';
}

export class ManualAdjustmentReasonCodes {
  static readonly Waste = 'Waste';
  static readonly FreezerTransfer = 'FreezerTransfer';
  static readonly Over = 'Over';
  static readonly Under = 'Under';
  static readonly DailyZero = 'DailyZero';
  static readonly Other = 'Other';

  static getAll(): { value: string; label: string }[] {
    return [
      { value: this.Waste, label: 'Waste' },
      { value: this.FreezerTransfer, label: 'Freezer Transfer' },
      { value: this.Over, label: 'Over' },
      { value: this.Under, label: 'Under' },
      { value: this.Other, label: 'Other' }
    ];
  }
}

export interface OversUndersSummaryItem {
  productId: number;
  productName: string;
  productCode: string;
  wasteTotal: number;
  freezerTransferTotal: number;
  overTotal: number;
  underTotal: number;
  netAdjustment: number;
}

export interface TogglePackedRequest {
  isPacked: boolean;
}

/** Generic server-side paged result wrapper. */
export interface PagedResult<T> {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  items: T[];
}

/** Query parameters for the paged orders endpoint. */
export interface OrdersPagedQuery {
  page: number;
  pageSize: number;
  status?: string;
  search?: string;
  customerId?: number;
  /** When true and no status filter is set, exclude Delivered and Cancelled. */
  activeOnly?: boolean;
  sortBy?: 'orderNumber' | 'customer' | 'orderDate' | 'deliveryDate' | 'status';
  sortDir?: 'asc' | 'desc';
}

/** Query parameters for the paged shipments endpoint. */
export interface ShipmentsPagedQuery {
  page: number;
  pageSize: number;
  status?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'shipmentNumber' | 'status' | 'customer' | 'driver' | 'shipped' | 'delivered';
  sortDir?: 'asc' | 'desc';
}

// ---- Order Templates ----

/** Wire representation of an order template (header + items). Mirrors
 *  OrderTemplateDto on the backend. ItemCount comes from the list endpoint;
 *  Items is populated only by the detail endpoint to keep the list payload
 *  small. */
export interface OrderTemplate {
  id: number;
  customerId: number;
  customerName?: string;
  templateName: string;
  description?: string;
  isActive: boolean;
  itemCount: number;
  items: OrderTemplateItem[];
  createdDate?: string;
  createdBy?: string;
  modifiedDate?: string;
  modifiedBy?: string;
}

export interface OrderTemplateItem {
  id: number;
  productId: number;
  productCode?: string;
  productName?: string;
  quantity: number;
  specialInstructions?: string;
  displayOrder: number;
}

export interface CreateOrderTemplateRequest {
  customerId: number;
  templateName: string;
  description?: string;
  items: OrderTemplateItemRequest[];
}

export interface UpdateOrderTemplateRequest {
  templateName: string;
  description?: string;
  isActive: boolean;
  items: OrderTemplateItemRequest[];
}

export interface OrderTemplateItemRequest {
  productId: number;
  quantity: number;
  specialInstructions?: string;
  displayOrder: number;
}

// ============ PRODUCTION HISTORY ============

export interface ProductionHistoryItem {
  productId: number;
  productName: string;
  productCode: string;
  category: string;
  quantityProduced: number;
}

// ============ PURCHASING & RAW MATERIAL STOCK CONTROL ============

export class PurchaseOrderStatuses {
  static readonly Draft = 'Draft';
  static readonly Submitted = 'Submitted';
  static readonly Ordered = 'Ordered';
  static readonly PartiallyReceived = 'Partially Received';
  static readonly Received = 'Received';
  static readonly Closed = 'Closed';
  static readonly Declined = 'Declined';
  static readonly Cancelled = 'Cancelled';
}

export interface Vendor {
  id: number;
  vendorName: string;
  vendorCode: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  paymentTerms?: string;
  sageVendorId?: string;
  notes?: string;
  isActive: boolean;
  createdDate?: Date;
  createdBy?: string;
  modifiedDate?: Date;
  modifiedBy?: string;
}

export interface RawMaterial {
  id: number;
  code: string;
  name: string;
  category?: string;
  /** Boxes, labels and other purchased non-ingredients (decision E.1). */
  isConsumable: boolean;
  /** Base unit stock is held and issued in (kg, L, Each) */
  unit: string;
  /** Unit the vendor sells in (e.g. "25kg bag") */
  purchaseUnit?: string;
  /** Base units per purchase unit (25 for a 25kg bag) */
  purchaseUnitSize: number;
  currentStock: number;
  reorderLevel: number;
  reorderQuantity?: number;
  preferredVendor?: Vendor;
  lastUnitCost: number;
  notes?: string;
  isActive: boolean;
  isBelowReorderLevel?: boolean;
}

/**
 * Outstanding quantity of one raw material on one live purchase order
 * (Draft / Submitted / Ordered / Partially Received). Per PO, so a screen editing
 * a PO can leave that PO out of its "already on order" figure.
 */
export interface RawMaterialOnOrderRow {
  rawMaterialId: number;
  purchaseOrderId: number;
  poNumber: string;
  status: string;
  /** Ordered minus received, in purchase units. */
  quantityOutstanding: number;
  /** The same outstanding quantity in the material's base unit. */
  baseUnitsOutstanding: number;
}

export interface PurchaseOrder {
  id: number;
  poNumber: string;
  vendor: Vendor;
  currentStatus: string;
  orderDate: Date;
  expectedDeliveryDate?: Date;
  vendorReference?: string;
  /** Floor request numbers this PO was generated from ("PR-00003, PR-00004"). */
  sourceRequestNumbers?: string;
  statusReason?: string;
  /**
   * Raised through Emergency purchase — goods that arrived with no PO behind them.
   * The order was created and received in one step, so it never sat in Draft.
   */
  isEmergency: boolean;
  notes?: string;
  totalCost: number;
  submittedDate?: Date;
  orderedDate?: Date;
  orderedBy?: string;
  createdDate?: Date;
  createdBy?: string;
}

export interface PurchaseOrderItem {
  id: number;
  rawMaterial: RawMaterial;
  quantityOrdered: number;
  unitCost: number;
  lineTotal: number;
  quantityReceived: number;
  notes?: string;
  quantityOutstanding: number;
  isFullyReceived: boolean;
}

export interface PurchaseOrderReceipt {
  id: number;
  receiptNumber: string;
  receivedDate: Date;
  receivedBy?: string;
  deliveryNoteNumber?: string;
  notes?: string;
}

export interface PurchaseOrderReceiptItem {
  id: number;
  purchaseOrderItem: PurchaseOrderItem;
  quantityReceived: number;
  quantityRejected: number;
  rejectionReason?: string;
}

export interface PurchaseOrderReceiptDetails {
  receipt: PurchaseOrderReceipt;
  items: PurchaseOrderReceiptItem[];
}

export interface PurchaseOrderDetails {
  order: PurchaseOrder;
  items: PurchaseOrderItem[];
  receipts: PurchaseOrderReceiptDetails[];
  /** Only on the response to posting a delivery: the GRN that post created. */
  postedReceiptId?: number;
}

export interface RawMaterialMovement {
  id: number;
  rawMaterial: RawMaterial;
  quantity: number;
  direction: string;
  movementType: string;
  reference?: string;
  department?: Department;
  reason?: string;
  balanceAfter: number;
  movementDate: Date;
  performedBy?: string;
}

export interface Department {
  id: number;
  name: string;
  notes?: string;
  isActive: boolean;
}

export interface RawMaterialCategory {
  id: number;
  name: string;
  isActive: boolean;
}

// Purchase requests (floor -> back office)

export class PurchaseRequestStatuses {
  static readonly Open = 'Open';
  static readonly Processed = 'Processed';
  static readonly Cancelled = 'Cancelled';
}

export interface PurchaseOrderRequest {
  id: number;
  requestNumber: string;
  status: string;
  notes?: string;
  statusReason?: string;
  createdDate: Date;
  createdBy?: string;
  processedDate?: Date;
  processedBy?: string;
  /** PO numbers this request was converted into. */
  processedReference?: string;
}

export interface PurchaseOrderRequestItem {
  id: number;
  rawMaterial: RawMaterial;
  quantity: number;
  notes?: string;
}

export interface PurchaseRequestDetails {
  request: PurchaseOrderRequest;
  items: PurchaseOrderRequestItem[];
}

export interface ConsolidatedRequestRow {
  rawMaterialId: number;
  materialName: string;
  materialCode: string;
  unit: string;
  purchaseUnit?: string;
  purchaseUnitSize: number;
  currentStock: number;
  reorderLevel: number;
  lastUnitCost: number;
  preferredVendorId?: number;
  preferredVendorName?: string;
  totalQuantity: number;
  requestNumbers: string[];
}

export interface PurchaseRequestLineRequest {
  rawMaterialId: number;
  quantity: number;
  notes?: string;
}

export interface SavePurchaseRequestRequest {
  notes?: string;
  lines: PurchaseRequestLineRequest[];
}

export interface ProcessRequestsOrderSpec {
  vendorId: number;
  expectedDeliveryDate?: string;
  notes?: string;
  items: PurchaseOrderItemRequest[];
}

export interface ProcessRequestsRequest {
  requestIds: number[];
  orders: ProcessRequestsOrderSpec[];
}

export interface SaveCategoryRequest {
  name: string;
  isActive: boolean;
}

export interface StockRequisition {
  id: number;
  reqNumber: string;
  department: Department;
  requestedBy?: string;
  issuedBy?: string;
  status: string;
  requestDate: Date;
  issuedDate?: Date;
  notes?: string;
}

export interface StockRequisitionItem {
  id: number;
  rawMaterial: RawMaterial;
  quantityRequested: number;
  quantityIssued: number;
  notes?: string;
}

export interface StockRequisitionDetails {
  requisition: StockRequisition;
  items: StockRequisitionItem[];
}

// Requests

export interface SaveVendorRequest {
  vendorName: string;
  /** Blank on create = auto-numbered (VEN-0001) by the backend. */
  vendorCode?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  paymentTerms?: string;
  sageVendorId?: string;
  notes?: string;
  isActive: boolean;
}

export interface SaveRawMaterialRequest {
  /** Blank on create = auto-numbered (RM-0001) by the backend. */
  code?: string;
  name: string;
  category?: string;
  isConsumable: boolean;
  unit: string;
  purchaseUnit?: string;
  purchaseUnitSize: number;
  reorderLevel: number;
  /** Base units to order at the reorder level; 0 = top up to twice the level */
  reorderQuantity?: number;
  preferredVendorId?: number;
  notes?: string;
  isActive: boolean;
}

export interface RawMaterialAdjustmentRequest {
  rawMaterialId: number;
  quantity: number;
  direction: string;
  movementType: string;
  reason: string;
}

export interface PurchaseOrderItemRequest {
  rawMaterialId: number;
  quantity: number;
  unitCost: number;
  notes?: string;
}

export interface SavePurchaseOrderRequest {
  vendorId: number;
  expectedDeliveryDate?: string;
  notes?: string;
  items: PurchaseOrderItemRequest[];
}

export interface ReceiptLineRequest {
  purchaseOrderItemId: number;
  quantityReceived: number;
  quantityRejected: number;
  rejectionReason?: string;
}

export interface ReceiveDeliveryRequest {
  deliveryNoteNumber?: string;
  notes?: string;
  lines: ReceiptLineRequest[];
}

/**
 * Emergency purchase: goods that arrived with no purchase order behind them.
 * The order is created and received in full in one step, so there is no expected
 * delivery date and no per-line received quantity — everything ordered is what
 * turned up. Quantities on the items are in purchase units, as on a normal PO.
 */
export interface EmergencyPurchaseRequest {
  vendorId: number;
  /** The vendor's delivery note or invoice number. */
  deliveryNoteNumber?: string;
  /** The vendor's own order reference, if they gave one. */
  vendorReference?: string;
  notes?: string;
  items: PurchaseOrderItemRequest[];
}

export interface SaveDepartmentRequest {
  name: string;
  notes?: string;
  isActive: boolean;
}

export interface RequisitionLineRequest {
  rawMaterialId: number;
  quantity: number;
  notes?: string;
}

export interface IssueStockRequest {
  departmentId: number;
  requestedBy?: string;
  notes?: string;
  lines: RequisitionLineRequest[];
}

// Goods receipts (GRN documents)

/** One row in the goods-receipts list, aggregated server-side. */
export interface GoodsReceiptSummary {
  receiptId: number;
  receiptNumber: string;
  receivedDate: Date;
  receivedBy?: string;
  deliveryNoteNumber?: string;
  purchaseOrderId: number;
  poNumber: string;
  vendorId: number;
  vendorName: string;
  lineCount: number;
  /** Lines on this delivery with any quantity rejected at the door. */
  rejectedLineCount: number;
  /** Quantity received x PO unit cost; 0 when the PO carried no costs. */
  receivedValue: number;
}

/** A goods received note line, flattened (QIN .Include is not nested). */
export interface GoodsReceiptLine {
  purchaseOrderItemId: number;
  materialCode?: string;
  materialName: string;
  /** Base unit stock is held in. */
  unit?: string;
  /** Unit the PO and this receipt are expressed in. */
  purchaseUnit?: string;
  purchaseUnitSize: number;
  quantityOrdered: number;
  /** Received on THIS delivery, in purchase units. */
  quantityReceived: number;
  quantityRejected: number;
  rejectionReason?: string;
  /** What this delivery booked into stock, in base units. */
  baseUnitsReceived: number;
  /** Running total received against the PO line across all deliveries. */
  receivedToDate: number;
  unitCost: number;
  lineValue: number;
}

export interface GoodsReceiptDetails {
  receipt: PurchaseOrderReceipt;
  purchaseOrder: PurchaseOrder;
  lines: GoodsReceiptLine[];
  /** 1-based position of this delivery among the PO's receipts. */
  deliverySequence: number;
  deliveryCount: number;
  totalReceivedValue: number;
}

// Stock counts (stocktake sessions)

export class StockCountStatuses {
  static readonly Counting = 'Counting';
  static readonly Recount = 'Recount';
  static readonly Submitted = 'Submitted';
  static readonly Posted = 'Posted';
  static readonly Cancelled = 'Cancelled';
}

export class StockCountMaterialScopes {
  static readonly All = 'All';
  static readonly Ingredients = 'Ingredients';
  static readonly Consumables = 'Consumables';
}

/**
 * One stock count. The variance fields are null while the count is blind
 * (Counting / Recount, or cancelled before it was submitted).
 */
export interface StockCountSummary {
  id: number;
  countNumber: string;
  status: string;
  materialScope: string;
  categoryScope?: string;
  vendorScopeId?: number | null;
  vendorScopeName?: string;
  notes?: string;
  statusReason?: string;
  recountRound: number;
  startedDate?: string;
  startedBy?: string;
  submittedDate?: string;
  submittedBy?: string;
  postedDate?: string;
  postedBy?: string;
  cancelledDate?: string;
  cancelledBy?: string;
  lineCount: number;
  countedLines: number;
  /** Lines sent back in the current recount round. */
  recountLines: number;
  recountedLines: number;
  postedLines: number;
  variancesVisible: boolean;
  varianceLines?: number | null;
  /** Sum of negative variance values (a negative number). */
  shrinkageValue?: number | null;
  overageValue?: number | null;
  netVarianceValue?: number | null;
}

/** A counting-sheet line. Carries no system quantity, cost or variance by design. */
export interface StockCountSheetLine {
  lineId: number;
  rawMaterialId: number;
  code: string;
  name: string;
  category?: string;
  unit: string;
  purchaseUnit?: string;
  purchaseUnitSize: number;
  isConsumable: boolean;
  vendorId?: number | null;
  vendorName?: string;
  isCounted: boolean;
  /** Base units; null = not counted (in a recount: not yet recounted). */
  quantity: number | null;
  countedBy?: string;
  countedDate?: string;
  notes?: string;
}

export interface StockCountSheet {
  session: StockCountSummary;
  /** Only the lines sent back for recount are on the sheet. */
  isRecount: boolean;
  lines: StockCountSheetLine[];
}

export interface StockCountReviewLine {
  lineId: number;
  rawMaterialId: number;
  code: string;
  name: string;
  category?: string;
  unit: string;
  purchaseUnit?: string;
  purchaseUnitSize: number;
  isConsumable: boolean;
  vendorId?: number | null;
  vendorName?: string;
  /** Per base unit. */
  unitCost: number;
  systemQuantityAtOpen: number;
  isCounted: boolean;
  /** The figure that posts: the recount if there is one, else the count. */
  countedQuantity?: number | null;
  /** System quantity captured with countedQuantity. */
  systemQuantity?: number | null;
  /** The first count, when a recount replaced it. */
  firstCountQuantity?: number | null;
  isRecounted: boolean;
  recountRequested: boolean;
  countedBy?: string;
  countedDate?: string;
  notes?: string;
  varianceQuantity?: number | null;
  varianceValue?: number | null;
  /** Net stock movement between the count starting and this line being counted (already allowed for). */
  movedBeforeCount?: number | null;
  /** Receipts, issues and adjustments on this material while the count was open. */
  movementCount: number;
  isPosted: boolean;
  /** Signed quantity actually booked; smaller than the variance when a write-off was limited by stock on hand. */
  postedAdjustment?: number | null;
}

export interface StockCountReview {
  session: StockCountSummary;
  lines: StockCountReviewLine[];
  countedLines: number;
  uncountedLines: number;
  varianceLines: number;
  shrinkageValue: number;
  overageValue: number;
  netVarianceValue: number;
  movedLines: number;
}

export interface CreateStockCountRequest {
  materialScope: string;
  category?: string;
  vendorId?: number;
  notes?: string;
}

export interface SaveStockCountLineRequest {
  /** Null clears the line back to uncounted (not zero). */
  quantity: number | null;
  notes?: string;
}

export interface StockCountVarianceMaterialRow {
  rawMaterialId: number;
  code: string;
  name: string;
  category?: string;
  unit: string;
  isConsumable: boolean;
  vendorId?: number | null;
  vendorName?: string;
  countSessions: number;
  linesCounted: number;
  varianceLines: number;
  netVarianceQuantity: number;
  shrinkageValue: number;
  overageValue: number;
  netVarianceValue: number;
}

export interface StockCountVarianceGroupRow {
  name: string;
  materials: number;
  linesCounted: number;
  varianceLines: number;
  shrinkageValue: number;
  overageValue: number;
  netVarianceValue: number;
}

export interface StockCountVarianceReport {
  sessionCount: number;
  shrinkageValue: number;
  overageValue: number;
  netVarianceValue: number;
  byMaterial: StockCountVarianceMaterialRow[];
  byCategory: StockCountVarianceGroupRow[];
  byVendor: StockCountVarianceGroupRow[];
}

export interface StockCountHistoryRow {
  sessionId: number;
  countNumber: string;
  status: string;
  countedDate?: string;
  countedBy?: string;
  systemQuantity: number;
  countedQuantity: number;
  varianceQuantity: number;
  varianceValue: number;
  isRecounted: boolean;
  isPosted: boolean;
  postedDate?: string;
  notes?: string;
}

export interface UncountedMaterialRow {
  rawMaterialId: number;
  code: string;
  name: string;
  category?: string;
  unit: string;
  isConsumable: boolean;
  vendorId?: number | null;
  vendorName?: string;
  currentStock: number;
  stockValue: number;
  lastCountNumber?: string;
  lastCountedDate?: string;
  /** Null when the material has never been counted. */
  daysSinceCount?: number | null;
}


// Build of materials & material requirements (Workstream E)

export class ProductStockTypes {
  /** Sold to customers (the default). */
  static readonly FinishedGood = 'FinishedGood';
  /** Made in-house and used in other products (dough); never offered for ordering. */
  static readonly SubAssembly = 'SubAssembly';
}

export class BomComponentTypes {
  static readonly Product = 'Product';
  static readonly RawMaterial = 'RawMaterial';
}

export interface BomLineDetail {
  lineId: number;
  /** Product or RawMaterial */
  componentType: string;
  componentId: number;
  code?: string;
  name: string;
  unit?: string;
  /** Product components */
  stockType?: string;
  hasBom: boolean;
  /** Raw material components */
  isConsumable: boolean;
  isInactive: boolean;
  /** Per batch, in the component's unit. */
  quantity: number;
  wastagePercent: number;
  notes?: string;
  /** Per 1 unit of the parent, including wastage. */
  quantityPerUnit: number;
  unitCost: number;
  costKnown: boolean;
  costPerUnit: number;
}

export interface BomUsage {
  productId: number;
  productCode?: string;
  productName: string;
  quantityPerUnit: number;
}

export interface BomDetails {
  productId: number;
  productCode?: string;
  productName: string;
  unit?: string;
  category?: string;
  stockType: string;
  isKit: boolean;
  hasBom: boolean;
  batchSize: number;
  notes?: string;
  modifiedBy?: string;
  modifiedDate?: string;
  lines: BomLineDetail[];
  costPerUnit: number;
  costComplete: boolean;
  usedIn: BomUsage[];
}

export interface BomSummaryRow {
  productId: number;
  lineCount: number;
  costPerUnit: number;
  costComplete: boolean;
}

export interface BomLineRequest {
  componentProductId?: number | null;
  componentRawMaterialId?: number | null;
  quantity: number;
  wastagePercent: number;
  notes?: string;
}

export interface SaveBillOfMaterialsRequest {
  batchSize: number;
  notes?: string;
  lines: BomLineRequest[];
}

export interface ProductRequirementRow {
  productId: number;
  productCode?: string;
  productName: string;
  category?: string;
  unit?: string;
  stockType: string;
  hasBom: boolean;
  /** Remaining quantity on customer orders in the window (kits expanded). */
  orderDemand: number;
  /** Needed by committed production of products that use it. */
  dependentDemand: number;
  onHand: number;
  spoOutstanding: number;
  openSpoNumbers: string[];
  /** Order demand the factory board will make (not covered by stock on hand). */
  boardProduction: number;
  /** Needed by parents, not covered by leftover stock or open SPOs: raise an SPO. */
  shortfall: number;
  usedBy: string[];
}

export interface MaterialRequirementRow {
  rawMaterialId: number;
  code: string;
  name: string;
  category?: string;
  isConsumable: boolean;
  isInactive: boolean;
  /** Base unit; all quantities are in it. */
  unit: string;
  purchaseUnit: string;
  purchaseUnitSize: number;
  preferredVendorName?: string;
  demand: number;
  onHand: number;
  onOrder: number;
  onRequest: number;
  reorderLevel: number;
  projected: number;
  shortfall: number;
  /** Purchase units to cover the demand and restore the reorder level. */
  suggestedPurchaseUnits: number;
  poNumbers: string[];
  requestNumbers: string[];
  usedBy: string[];
}

export interface MaterialRequirements {
  horizonDays: number;
  deliveriesBefore: string;
  orderCount: number;
  openSpoCount: number;
  productShortfalls: number;
  materialShortfalls: number;
  products: ProductRequirementRow[];
  materials: MaterialRequirementRow[];
}

export interface RequirementRaiseLine {
  /** Product id (SPOs) or raw material id (purchase request). */
  id: number;
  /** Product units, or purchase units for raw materials. */
  quantity: number;
  /** The shortfall on screen, so the server can refuse if someone already raised it. */
  expectedShortfall: number;
}

// Reports (Workstream F)

export interface ReportOption {
  value: string;
  label: string;
}

export interface ReportParameter {
  key: string;
  label: string;
  /** date, select, number, bool */
  type: string;
  /** As a string: dates yyyy-MM-dd, bools true/false. */
  default: string;
  options?: ReportOption[];
}

export interface ReportDefinition {
  key: string;
  title: string;
  category: string;
  description: string;
  parameters: ReportParameter[];
}

export interface ReportColumn {
  key: string;
  label: string;
  /** text, number, money, percent, date, link */
  type: string;
  decimals: number;
  total: boolean;
}

export interface ReportSummaryItem {
  label: string;
  value: string;
}

export interface ReportResult {
  key: string;
  title: string;
  category: string;
  generatedAt: string;
  parameters: ReportSummaryItem[];
  summary: ReportSummaryItem[];
  columns: ReportColumn[];
  /** Keyed by column key; optional _highlight (danger/warning/success) and _links (column key -> route). */
  rows: { [key: string]: any }[];
  totals: { [key: string]: any };
  /** Rows arrive sorted by this column; shown as group headings. */
  groupColumn?: string;
  notes: string[];
  truncated: boolean;
}

// KPI checklists (Workstream G)

export class KpiFrequencies {
  static readonly Daily = 'Daily';
  static readonly Weekly = 'Weekly';
  static readonly Monthly = 'Monthly';
}

/** Roles a checklist task can be owed by, in display order. */
export const KPI_ROLES = ['Admin', 'Staff', 'FactoryManager', 'FactoryWorker', 'StockController', 'Driver'];

export interface KpiTask {
  id: number;
  name: string;
  description?: string;
  role: string;
  taskGroup?: string;
  frequency: string;
  /** HH:mm, factory-local */
  dueTime: string;
  /** ISO days 1 (Mon) .. 7 (Sun), comma-separated */
  daysOfWeek?: string;
  /** Monthly: 1-28, or 0 for the last day */
  dayOfMonth: number;
  valueLabel?: string;
  requiresValue: boolean;
  checkRange: boolean;
  valueMin: number;
  valueMax: number;
  displayOrder: number;
  isActive: boolean;
}

export interface KpiChecklistItem {
  instanceId: number;
  taskId: number;
  taskName: string;
  description?: string;
  role: string;
  taskGroup?: string;
  frequency: string;
  periodStart: string;
  periodEnd: string;
  /** Factory-local */
  dueDateTime: string;
  status: string;
  /** Pending, DueSoon, Overdue, Missed, Done, DoneLate, NotApplicable */
  displayStatus: string;
  canComplete: boolean;
  completedBy?: string;
  completedAt?: string;
  isLate: boolean;
  value?: number | null;
  outOfRange: boolean;
  notes?: string;
  valueLabel?: string;
  requiresValue: boolean;
  checkRange: boolean;
  valueMin: number;
  valueMax: number;
  displayOrder: number;
}

export interface KpiChecklist {
  date: string;
  /** Factory-local now at the server */
  now: string;
  roles: string[];
  isSupervisor: boolean;
  items: KpiChecklistItem[];
  done: number;
  open: number;
  overdue: number;
}

export interface KpiRoleBoard {
  role: string;
  items: KpiChecklistItem[];
  total: number;
  doneCount: number;
  doneLateCount: number;
  pendingCount: number;
  dueSoonCount: number;
  overdueCount: number;
  missedCount: number;
  notApplicableCount: number;
  outOfRangeCount: number;
  completionPercent?: number | null;
  onTimePercent?: number | null;
}

export interface KpiBoard {
  date: string;
  now: string;
  roles: KpiRoleBoard[];
  total: number;
  doneCount: number;
  overdueCount: number;
  missedCount: number;
  outOfRangeCount: number;
  completionPercent?: number | null;
  onTimePercent?: number | null;
}

// AI report engine ("Ask the data")

export interface AiStatus {
  /** ANTHROPIC_API_KEY is set on the server; false means the screen can say so up front. */
  configured: boolean;
  /** How many reports this admin's roles can reach. */
  reportCount: number;
}

export interface AiConversationSummary {
  id: number;
  title: string;
  createdBy: string;
  isShared: boolean;
  /** False for a conversation another admin shared — read-only, duplicate to continue. */
  isMine: boolean;
  createdDate: string;
  lastMessageDate: string;
  tokensUsed: number;
  tokenBudget: number;
}

export interface AiMessageView {
  id: number;
  sequence: number;
  /** user or assistant */
  role: string;
  content: string;
  /** JSON string, may be null — parsed by the page into AiToolCallLog[]. */
  toolCalls: string;
  generatedHtml: string;
  generatedHtmlTitle: string;
  createdDate: string;
}

export interface AiConversationDetail {
  conversation: AiConversationSummary;
  messages: AiMessageView[];
}

/** One report the model ran while answering; parsed out of AiMessageView.toolCalls. */
export interface AiToolCallLog {
  report: string;
  key: string;
  parameters: { [key: string]: string };
  rows: number;
}

/** A question an admin saved to re-ask; shared prompts are usable by every other admin. */
export interface AiPrompt {
  id: number;
  name: string;
  promptText: string;
  createdBy: string;
  createdByUserId: number;
  isShared: boolean;
  createdDate: string;
}
